import * as Sentry from '@sentry/browser';
import config from 'config';

import { ProductType } from '~/techstyle-shared/react-products';

const cdnUrl = config.get('public.cdn');

function generateSkuWithoutSize({ alias, itemNumber }) {
  // Some products like the membership will not have an item number!
  // (/account/wishlist) Product Sets need to use `product.alias` since they don’t have `bundleItems`
  if (!itemNumber && !alias) {
    return null;
  }

  // Parse off the last dash stuff...
  const itemArray = itemNumber && (itemNumber + '').split('-');
  const skuWithoutSize = itemNumber ? itemArray.slice(0, -1).join('-') : alias;

  return skuWithoutSize;
}

const imageSizesObject = {
  1: { width: 200, height: 200 },
  2: { width: 400, height: 400 },
  3: { width: 800, height: 800 },
  4: { width: 1600, height: 1600 },
  5: { width: 3000, height: 3000 },
  6: { width: 150, height: 200 },
  7: { width: 300, height: 400 },
  8: { width: 600, height: 800 },
  9: { width: 1200, height: 1600 },
  10: { width: 2250, height: 3000 },
};

const generateGiftCardImage = ({ product, angle, imageSize }) => {
  const label = product.alias.split(' ');

  if (label.length < 2) {
    Sentry.captureMessage(
      `Error: 'generateCdnImageUrl' could not get label`,
      'warning'
    );
    return null;
  }

  const permalink = product.permalink;

  if (!permalink) {
    Sentry.captureMessage(
      `Error: 'generateCdnImageUrl' could not find permalink for the following product: ${product}`,
      'warning'
    );
    return null;
  }
  return `${cdnUrl}/media/images/products/${label[0]}/${permalink}-${angle}-${imageSizesObject[imageSize].width}x${imageSizesObject[imageSize].height}.jpg`;
};

const generateSxfCdnImageUrl = ({
  product,
  modelIndex = 0,
  angle = 1, // 1-5 or laydown
  imageSize = 2,
  withWidthHeight = false,
}) => {
  if (product.productTypeId === ProductType.GIFT_CERTIFICATE) {
    return generateGiftCardImage({
      product,
      angle: 1,
      imageSize,
    });
  } else if (product.productTypeId === ProductType.MEMBERSHIP_FEE_PLACEHOLDER) {
    return `${cdnUrl}${config.get(
      'public.membershipProductImage.thumbnailURL'
    )}`;
  }

  const itemNumber = product.item_number || product.itemNumber;
  const skuWithoutSize = generateSkuWithoutSize({
    alias: product.alias,
    itemNumber,
  });

  if (!skuWithoutSize) {
    Sentry.captureException(
      `Error: 'generateCdnImageUrl' could not find the sku for the following product: ${product}`
    );
    return null;
  }

  let permalink = product.permalink;

  if (!permalink) {
    const useMasterLabel =
      product.masterLabel && product.masterLabel.toUpperCase();
    const useLabel = product.label && product.label.toUpperCase();
    let label = useMasterLabel || useLabel;

    if (label) {
      label = label.replace(/ /g, '-');
      permalink = `${label}-${skuWithoutSize}`;
    } else {
      Sentry.captureMessage(
        `Error: 'generateCdnImageUrl' could not find permalink for the following product: ${product}`,
        'warning'
      );
      return null;
    }
  }

  const url = `${cdnUrl}/media/images/products/${skuWithoutSize.toUpperCase()}/${permalink.toUpperCase()}${
    modelIndex ? `-alt${modelIndex}` : ''
  }-${angle.toString().toUpperCase()}-${imageSizesObject[imageSize].width}x${
    imageSizesObject[imageSize].height
  }.jpg`;

  if (withWidthHeight) {
    return {
      url,
      ...imageSizesObject[imageSize],
    };
  }

  return url;
};

export default generateSxfCdnImageUrl;
